import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Shared by every Pro plan — shown once above the cards instead of repeated on each one.
const sharedFeatures = [
  "Unlimited players per room",
  "Unlimited active rooms",
  "All 6 theme presets (Game Show, Classroom, Sports, Retro, Ocean)",
  "Live scoreboard + kick + suspend",
  "End game reveal for all players",
  "Mobile screen wake lock",
];

const UpgradePage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [submitting, setSubmitting] = useState(null); // "monthly" | "lifetime" | null
  const [error, setError] = useState("");

  const handleCheckout = async (plan) => {
    setError("");
    if (!user) {
      // Guests can browse pricing freely; only checkout requires an account.
      navigate("/login");
      return;
    }
    setSubmitting(plan);
    try {
      const { data } = await axios.get(`${API}/payments/checkout-url`, { params: { plan } });
      window.location.href = data.url;
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to start checkout");
      setSubmitting(null);
    }
  };

  if (loading) return null;

  const tier = user ? user.pro_tier || (user.is_pro ? "lifetime" : "free") : "free";
  if (tier === "monthly" || tier === "lifetime") {
    return (
      <div className="min-h-screen w-full bg-black text-white flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="inline-block px-4 py-1 bg-[#FFEA00] text-black text-sm font-bold tracking-wider rounded-full mb-4">
            {tier === "lifetime" ? "PRO · LIFETIME" : "PRO · MONTHLY"}
          </div>
          <h1
            className="text-6xl text-white mb-3"
            style={{ fontFamily: "Anton, sans-serif" }}
            data-testid="already-pro-title"
          >
            YOU'RE PRO!
          </h1>
          <p className="text-[#A1A1AA] mb-6">
            All features unlocked. Time to host some games.
          </p>
          <Link
            to="/"
            className="inline-block px-6 py-3 bg-[#00F0FF] text-black font-bold rounded-full hover:scale-105 transition-transform"
            data-testid="back-home-button"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full relative overflow-x-hidden text-white">
      <div
        className="fixed inset-0 z-[-1]"
        style={{
          backgroundImage: `url('/bg-hero.png')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      <div className="relative z-10 max-w-5xl mx-auto px-4 py-8 sm:py-12">
        <Link
          to="/"
          className="text-[#A1A1AA] hover:text-white text-sm flex items-center gap-1"
          data-testid="upgrade-back-button"
        >
          ← Back
        </Link>

        <div className="text-center mt-6 mb-10">
          <p className="text-[#00F0FF] tracking-[0.3em] text-xs font-semibold mb-2">
            UPGRADE TO PRO
          </p>
          <h1
            className="text-5xl sm:text-6xl text-white mb-3"
            style={{ fontFamily: "Anton, sans-serif" }}
          >
            UNLOCK EVERYTHING
          </h1>
          <p className="text-[#A1A1AA] max-w-md mx-auto">
            You're on the free plan. Choose a Pro plan to host unlimited players, rooms, and unlock all themes.
          </p>
        </div>

        {/* Shared features — every Pro plan includes these, shown once instead of repeated per card */}
        <div className="max-w-3xl mx-auto mb-6">
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
            {sharedFeatures.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm">
                <span className="text-[#00F0FF] mt-0.5">✓</span>
                <span className="text-white/80">{f}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Plan cards */}
        <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
          {/* MONTHLY */}
          <div
            className="relative bg-black/50 backdrop-blur-md border-2 border-[#00F0FF]/40 rounded-3xl p-6 sm:p-8 hover:border-[#00F0FF] transition-all"
            data-testid="plan-card-monthly"
          >
            <div className="absolute -top-3 left-6 px-3 py-1 bg-[#00F0FF] text-black text-xs font-bold tracking-wider rounded-full">
              MOST POPULAR
            </div>
            <h3
              className="text-3xl mb-1 text-[#00F0FF]"
              style={{ fontFamily: "Anton, sans-serif" }}
            >
              PRO MONTHLY
            </h3>
            <p className="text-[#A1A1AA] text-sm mb-4">Pay as you go</p>
            <div className="flex items-baseline gap-2 mb-1">
              <span
                className="text-6xl text-white tabular-nums"
                style={{ fontFamily: "Anton, sans-serif" }}
              >
                $6.99
              </span>
              <span className="text-[#A1A1AA]">/ month</span>
            </div>
            <p className="text-xs text-[#A1A1AA] mb-8">Cancel anytime · No commitment</p>

            <button
              onClick={() => handleCheckout("monthly")}
              disabled={submitting !== null}
              className="w-full py-4 bg-[#00F0FF] text-black font-bold text-lg rounded-xl shadow-[0_0_20px_rgba(0,240,255,0.4)] hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="checkout-monthly-button"
            >
              {submitting === "monthly" ? "Redirecting..." : "Start Monthly · $6.99"}
            </button>
          </div>

          {/* LIFETIME */}
          <div
            className="relative bg-gradient-to-br from-[#FFEA00]/10 to-[#FF0055]/10 backdrop-blur-md border-2 border-[#FFEA00]/40 rounded-3xl p-6 sm:p-8 hover:border-[#FFEA00] transition-all"
            data-testid="plan-card-lifetime"
          >
            <div className="absolute -top-3 left-6 px-3 py-1 bg-[#FFEA00] text-black text-xs font-bold tracking-wider rounded-full">
              BEST VALUE
            </div>
            <h3
              className="text-3xl mb-1 text-[#FFEA00]"
              style={{ fontFamily: "Anton, sans-serif" }}
            >
              PRO LIFETIME
            </h3>
            <p className="text-[#A1A1AA] text-sm mb-4">One-time payment, yours forever</p>
            <div className="flex items-baseline gap-2 mb-1">
              <span
                className="text-6xl text-white tabular-nums"
                style={{ fontFamily: "Anton, sans-serif" }}
              >
                $49
              </span>
              <span className="text-[#A1A1AA]">once</span>
            </div>
            <p className="text-xs text-[#FFEA00] mb-8">
              Saves ~$35 vs 1 year of monthly · No renewals
            </p>

            <button
              onClick={() => handleCheckout("lifetime")}
              disabled={submitting !== null}
              className="w-full py-4 bg-[#FFEA00] text-black font-bold text-lg rounded-xl shadow-[0_0_20px_rgba(255,234,0,0.4)] hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="checkout-lifetime-button"
            >
              {submitting === "lifetime" ? "Redirecting..." : "Get Lifetime · $49"}
            </button>
          </div>
        </div>

        {error && (
          <div
            className="mt-6 max-w-md mx-auto bg-[#FF0055]/10 border border-[#FF0055]/30 text-[#FF0055] text-center rounded-xl px-4 py-3 text-sm"
            data-testid="upgrade-error"
          >
            {error}
          </div>
        )}

        <p className="text-center text-[#A1A1AA] text-xs mt-8">
          Secure checkout · Powered by Gumroad
        </p>
      </div>
    </div>
  );
};

export default UpgradePage;
